import { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import mint from '@tunein/mint';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import CommonDialog from '../shared/dialog/CommonDialog';
import CaretDown from '../shared/svgIcons/CaretDown';
import {
  expandPrerollPlayer,
  minimizePrerollPlayer,
} from '../../actions/preroll';
import { VIDEO_PREROLL_DISABLE_SKIP_KEY } from '../../constants/experiments/ads';
import { ADVERTISEMENT } from '../../constants/localizations/ads';
import PrerollViewContainer from './PrerollViewContainer';
import css from './preroll.module.scss';

const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 480;

const baseStyles = (width, height) => ({
  content: {
    // Adding on the width of the border
    width: width + 32,
  },
  body: {
    width,
    minHeight: height + 72,
    maxHeight: height + 72,
    borderWidth: '0px 16px 0px 16px',
  },
  title: {
    fontSize: 13,
    padding: '2px 16px 2px 16px',
  },
});

const minimizedStyles = (width, height) => ({
  // Minimized styling is half the length of regular styling
  dialog: {
    pointerEvents: 'none',
  },
  content: {
    pointerEvents: 'auto',
    position: 'absolute',
    // Adding on the width of the border
    width: width / 2 + 8,
    bottom: 152,
    right: 5,
  },
  body: {
    width: width / 2,
    minHeight: height / 2 + 36,
    maxHeight: height / 2 + 36,
    borderWidth: '0px 4px 0px 4px',
  },
  title: {
    fontSize: 12,
    padding: '2px 16px 2px 16px',
  },
  overlay: {
    display: 'none',
  },
  icon: {
    // when minimized, the down caret is flipped to an upcaret
    transform: 'rotate(180deg)',
  },
});

// exported for unit testing
export class PrerollDialog extends Component {
  static propTypes = {
    breakpoint: PropTypes.number.isRequired,
    prerollDialogOpen: PropTypes.bool.isRequired,
    isMinimized: PropTypes.bool.isRequired,
    prerollActions: PropTypes.object.isRequired,
    videoHeight: PropTypes.number,
    videoWidth: PropTypes.number,
    [VIDEO_PREROLL_DISABLE_SKIP_KEY]: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  componentDidMount() {
    mint.updateState('isPrerollPlayerMinimized', this.props.isMinimized);
  }

  componentDidUpdate(prevProps) {
    const { videoHeight, videoWidth, isMinimized, prerollDialogOpen } =
      this.props;
    const height = videoHeight || DEFAULT_HEIGHT;
    const width = videoWidth || DEFAULT_WIDTH;

    if (!prevProps.prerollDialogOpen && prerollDialogOpen && isMinimized) {
      mint.resize(width / 2, height / 2);
    }

    if (prevProps.prerollDialogOpen && !prerollDialogOpen && isMinimized) {
      this.props.prerollActions.expandPrerollPlayer();
    }
  }

  handleMinimize = () => {
    const { videoWidth, videoHeight } = this.props;
    const width = videoWidth || DEFAULT_WIDTH;
    const height = videoHeight || DEFAULT_HEIGHT;

    if (this.props.isMinimized) {
      this.props.prerollActions.expandPrerollPlayer();
      mint.resize(width, height);
      mint.updateState('isPrerollPlayerMinimized', false);
    } else {
      this.props.prerollActions.minimizePrerollPlayer();
      mint.resize(width / 2, height / 2);
      mint.updateState('isPrerollPlayerMinimized', true);
    }
  };

  render() {
    const {
      prerollDialogOpen,
      breakpoint,
      isMinimized,
      videoHeight,
      videoWidth,
      [VIDEO_PREROLL_DISABLE_SKIP_KEY]: disableSkipVideoPreroll,
    } = this.props;

    const height = videoHeight || DEFAULT_HEIGHT;
    const width = videoWidth || DEFAULT_WIDTH;
    const styles = isMinimized
      ? minimizedStyles(width, height)
      : baseStyles(width, height);

    return (
      <CommonDialog
        className="prerollDialog"
        key="prerollDialog"
        style={
          prerollDialogOpen
            ? styles.dialog
            : {
                ...styles.dialog,
                display: 'none',
              }
        }
        contentClassName={css.prerollDialogContent}
        dialogContentStyle={styles.content}
        bodyClassName={css.prerollDialogBody}
        bodyStyle={styles.body}
        modal={false}
        autoScrollBodyContent={false}
        autoDetectWindowHeight={false}
        overlayClassName="prerollDialogOverlay"
        overlayStyle={styles.overlay}
        title={this.context.getLocalizedText(ADVERTISEMENT)}
        titleClassName={css.prerollDialogTitle}
        titleStyle={styles.title}
        dialogOpen
        hideCornerClose
        useWideModalWidth
      >
        {!disableSkipVideoPreroll && (
          <div
            onClick={this.handleMinimize}
            className={css.minimizeButtonContainer}
            data-testid="dialogMinimize"
          >
            <CaretDown
              key="dialogMinimize"
              className={css.minimizeButton}
              style={styles.icon}
            />
          </div>
        )}
        <PrerollViewContainer
          breakpoint={breakpoint}
          width={width}
          isMinimized={isMinimized}
        />
      </CommonDialog>
    );
  }
}

export function mapStateToProps(state) {
  const { prerollDialogOpen, isMinimized, videoHeight, videoWidth } =
    state.preroll;

  return {
    prerollDialogOpen,
    isMinimized,
    videoHeight,
    videoWidth,
  };
}

export function mapDispatchToProps(dispatch) {
  const actions = {
    expandPrerollPlayer,
    minimizePrerollPlayer,
  };

  return {
    prerollActions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connectWithExperiments([VIDEO_PREROLL_DISABLE_SKIP_KEY]),
  connect(mapStateToProps, mapDispatchToProps),
)(PrerollDialog);
